import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGuestsOptional } from "@/providers/GuestsProvider";
import { runPartyCheckIn, type PartyCheckInReport } from "./checkInParty";
import { invalidateAfterCheckIn } from "./invalidateAfterCheckIn";

/**
 * Write a party's check-in from a surface that may or may not sit inside a
 * `GuestsProvider`.
 *
 * The party sheet used to read `useGuests()`, which HARD-THROWS without the
 * provider — and the provider is mounted on exactly three surfaces (guest list,
 * requests page, `StandaloneGuestDetail`). The check-in history page is not one
 * of them, so opening the sheet from either of its doors would have taken the
 * page down rather than checking anybody in.
 *
 * The two paths differ only in what they can reach afterwards:
 *  - INSIDE the provider, `checkInGuests` also patches the rows it owns (top
 *    level and nested companions) and broadcasts `guestDataUpdated`, so an open
 *    detail popup follows along;
 *  - OUTSIDE it there are no rows to patch, and the caches those rows come from
 *    are marked stale instead — the same call the provider's own
 *    `invalidateContingent` makes.
 *
 * Both dispatch `checkInActionCompleted` exactly once per run, and only when
 * something actually moved: the listeners behind it are refetches.
 */
export function usePartyCheckInWriter(eventId: string) {
  const guestsCtx = useGuestsOptional();
  const queryClient = useQueryClient();

  return useCallback(
    async (
      guestIds: string[],
      checkIn: boolean,
      performedByType?: string,
    ): Promise<PartyCheckInReport> => {
      if (guestsCtx) {
        return guestsCtx.checkInGuests(guestIds, checkIn, performedByType);
      }
      const report = await runPartyCheckIn(
        eventId,
        guestIds,
        checkIn,
        performedByType,
      );
      if (report.results.some((r) => r.status !== "failed")) {
        invalidateAfterCheckIn(queryClient, eventId);
        window.dispatchEvent(new Event("checkInActionCompleted"));
      }
      return report;
    },
    [guestsCtx, queryClient, eventId],
  );
}
